/**
 * Service responsible for managing and tracking all flow variables in the flow.
 * This service provides a centralized way to access variables from custom actions,
 * content actions, and configuration across the entire application.
 */
export class FlowVariablesService {
    static $inject = ['VariablesService'];
    constructor(VariablesService) {
        this.outputVariables = new Map();
        this.listeners = [];
        this.variablesService = VariablesService;
    }
    /**
     * Updates the flow variables list from the current flow
     * @param flow The current flow view model
     * @param configuration Optional flow configuration
     */
    updateFromFlow(flow, configuration) {
        if (!flow) {
            this.clear();
            return;
        }
        // Clear existing variables
        this.outputVariables.clear();
        // Iterate through all states in the flow
        Object.keys(flow).forEach(stateId => {
            const state = flow[stateId];
            if (!state) {
                return;
            }
            const entries = [];
            // Process custom actions (entering + leaving)
            const customActions = [
                ...(state.$enteringCustomActions || []),
                ...(state.$leavingCustomActions || []),
            ];
            const customActionVars = this.variablesService.customActionVariables(customActions);
            customActionVars.forEach(variableName => {
                // Find which action generated this variable
                const action = customActions.find(a => this.getVariableFromAction(a) === variableName);
                entries.push({
                    stateId: state.id,
                    stateName: state.$title || state.id,
                    variableName,
                    source: 'customAction',
                    actionType: action?.type || 'Unknown',
                });
            });
            // Process content actions
            const contentActions = state.$contentActions || [];
            const contentActionVars = this.variablesService.contentActionsVariables(contentActions);
            contentActionVars.forEach(variableName => {
                entries.push({
                    stateId: state.id,
                    stateName: state.$title || state.id,
                    variableName,
                    source: 'contentAction',
                });
            });
            if (entries.length > 0) {
                this.outputVariables.set(stateId, entries);
            }
        });
        // Add configuration variables (not tied to a specific state)
        if (configuration) {
            const configVars = this.variablesService.configurationVariables(configuration);
            if (configVars.length > 0) {
                const configEntries = configVars.map(variableName => ({
                    stateId: 'configuration',
                    stateName: 'Configuration',
                    variableName,
                    source: 'configuration',
                }));
                this.outputVariables.set('configuration', configEntries);
            }
        }
        // Notify listeners
        this.notifyListeners();
    }
    /**
     * Helper method to extract variable name from an action
     */
    getVariableFromAction(action) {
        if (!action?.settings) {
            return null;
        }
        switch (action.type) {
            case 'ProcessHttp':
                return action.settings.responseBodyVariable || action.settings.responseStatusVariable;
            case 'ExecuteScript':
            case 'ExecuteScriptV2':
            case 'ExecuteBlipFunction':
            case 'ExecuteTemplate':
                return action.settings.outputVariable;
            case 'SetVariable':
                return action.settings.variable;
            case 'SendCommand':
                return action.settings.responseBodyVariable || action.settings.responseStatusVariable;
            default:
                return null;
        }
    }
    /**
     * Gets all output variables for a specific state
     * @param stateId The state identifier
     * @returns Array of output variable entries for the state
     */
    getVariablesByStateId(stateId) {
        return this.outputVariables.get(stateId) || [];
    }
    /**
     * Gets all output variables from all states as a flat array
     * @returns Array of all output variable entries
     */
    getAllVariables() {
        const allVariables = [];
        this.outputVariables.forEach(entries => {
            allVariables.push(...entries);
        });
        return allVariables;
    }
    /**
     * Gets all output variable names as an array
     * @returns Array of all output variable names
     */
    getAllVariableNames() {
        return this.getAllVariables().map(entry => entry.variableName);
    }
    /**
     * Gets the total count of output variables
     * @returns The number of output variables
     */
    getCount() {
        let count = 0;
        this.outputVariables.forEach(entries => {
            count += entries.length;
        });
        return count;
    }
    /**
     * Checks if a state has output variables
     * @param stateId The state identifier
     * @returns True if the state has outputs, false otherwise
     */
    hasVariables(stateId) {
        const entries = this.outputVariables.get(stateId);
        return entries !== undefined && entries.length > 0;
    }
    /**
     * Clears all output variables
     */
    clear() {
        this.outputVariables.clear();
        this.notifyListeners();
    }
    /**
     * Subscribes to output variable changes
     * @param callback Function to be called when variables change
     * @returns Unsubscribe function
     */
    subscribe(callback) {
        this.listeners.push(callback);
        // Immediately call with current state
        callback(this.getAllVariables());
        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }
    /**
     * Notifies all listeners of variable changes
     */
    notifyListeners() {
        const variables = this.getAllVariables();
        this.listeners.forEach(listener => listener(variables));
    }
    /**
     * Gets a formatted list of variables for display purposes
     * @returns Array of formatted strings with state name and variable
     */
    getFormattedVariablesList() {
        return this.getAllVariables().map(entry => {
            const sourceLabel = entry.source === 'configuration'
                ? 'Configuration'
                : entry.actionType || entry.source || 'Unknown';
            return `${entry.stateName} - ${sourceLabel}: {{${entry.variableName}}}`;
        });
    }
    /**
     * Searches for variables by term (state name or variable name)
     * @param term The search term
     * @returns Filtered array of flow variable entries
     */
    searchVariables(term) {
        if (!term || term.trim() === '') {
            return this.getAllVariables();
        }
        const searchTerm = term.toLowerCase();
        return this.getAllVariables().filter(entry => {
            const stateName = entry.stateName.toLowerCase();
            const variableName = entry.variableName.toLowerCase();
            const source = (entry.source || '').toLowerCase();
            const actionType = (entry.actionType || '').toLowerCase();
            return stateName.includes(searchTerm) ||
                variableName.includes(searchTerm) ||
                source.includes(searchTerm) ||
                actionType.includes(searchTerm);
        });
    }
    /**
     * Exports all variables as a JSON object
     * @returns Object mapping state IDs to their flow variables
     */
    exportAsJson() {
        const result = {};
        this.outputVariables.forEach((entries, stateId) => {
            result[stateId] = entries.map(entry => ({
                variable: entry.variableName,
                source: entry.source,
                actionType: entry.actionType,
            }));
        });
        return result;
    }
}
FlowVariablesService.$inject = ['VariablesService'];
